import * as PopUp from "../../../components/deprecated/Popup/deprecated_PopUps";
import { FirebaseEvents } from "../../../utils/firebaseEvents";

import { UserData } from "../User/UserData";
import { CreatedByData } from "./CreatedByData";
import { FirebaseData } from "./FirebaseData";
import { deleteObject, getDownloadURL, getMetadata, getStorage, ref, uploadBytes, uploadString } from "firebase/storage";
import { t } from "i18next";
import { downloadFirebaseFile } from "./file.utils";
import { ToastUtilities } from "@libs/react-toastify/ToastUtilities";
import { Toast } from "react-toastify/dist/components";

export class FirebaseFile {
  id: string = "";
  title: string = "";
  fileType: string = "";
  size?: number;
  externalURL?: string;
  path?: string;

  fileURL?: string;
  dataURL?: string;

  globalFileID?: string;

  createdBy: CreatedByData = {};

  loaded: boolean = false;
  deleted: boolean = false;
  loading: boolean = false;
  private loadedListeners: (() => void)[] = [];

  constructor(path?: string) {
    this.path = path ? path : "";
    this.downloadFile = this.downloadFile.bind(this);
  }

  setCreatedBy(createdBy: CreatedByData | undefined, user: UserData | undefined, date: Date | undefined) {
    this.createdBy = {
      date: createdBy?.date ? createdBy.date : date,
      user: createdBy?.user ? createdBy.user : user,
    };
  }

  // Static initializers
  static initialize(id: string, data: { [key: string]: any }): FirebaseFile {
    const file = new FirebaseFile();
    file.id = id;
    file.path = typeof data.path === "string" ? data.path : "";
    file.title = typeof data.name === "string" ? data.name : "";
    file.globalFileID = typeof data.globalFileID === "string" ? data.globalFileID : undefined;
    file.fileType = typeof data.fileType === "string" ? data.fileType : typeof data.type === "string" ? data.type : "";
    file.size = typeof data.size === "number" ? data.size : undefined;
    file.externalURL = typeof data.externalURL === "string" ? data.externalURL : undefined;
    if (file.externalURL) {
      file.loaded = true;
    }

    if (!file.fileType && file.title.split(".").length > 0) {
      const splits = file.title.split(".");
      file.fileType = splits[splits.length - 1];
    }

    return file;
  }
  static initializeFromFile(file: File, folderPath: string, filesInFolder: FirebaseFile[]): Promise<FirebaseFile> {
    return new Promise((resolve: (value: FirebaseFile) => void) => {
      let fileType = file.name.split(".")[file.name.split(".").length - 1];

      let existingName = 1;
      for (const existingFile of filesInFolder) {
        let existingFileTitle = file.name.split("." + fileType);
        if (existingFile.title?.includes(existingFileTitle[0])) existingName++;
      }

      let fileName = file.name.split(".")[0];
      if (existingName > 1) fileName = fileName + "(" + existingName + ")";

      let firebaseFile = new FirebaseFile(folderPath + "/" + fileName + "." + fileType);
      firebaseFile.fileType = fileType;
      firebaseFile.title = fileName + "." + fileType;
      firebaseFile.id = FirebaseData.generateID();

      let reader = new FileReader();
      reader.onloadend = () => {
        if (reader.result as string) {
          firebaseFile.fileURL = reader.result as string;
          resolve(firebaseFile);
        }
      };
      reader.readAsDataURL(file);
    });
  }

  // Loading
  loadFileURL(callback: () => void): void {
    const storage = getStorage();
    this.loadedListeners.push(callback);
    if (!this.loading && this.path) {
      this.loading = true;
      const fileRef = ref(storage, this.path);
      getDownloadURL(fileRef)
        .then((url) => {
          if (typeof url === "string") {
            this.fileURL = url;
          } else {
            this.deleted = true;
          }
        })
        .catch(() => {
          this.deleted = true;
        })
        .finally(() => {
          this.loading = false;
          this.loaded = true;
          for (const listener of this.loadedListeners) {
            listener();
          }
        });
    }
  }

  loadStorageMetadata(callback: () => void) {
    const storage = getStorage();

    if (this.path) {
      const reference = ref(storage, this.path);
      getMetadata(reference)
        .then((metadata) => {
          if (!this.size && typeof metadata.size === "number") this.size = metadata.size;
          if (!this.createdBy.date && typeof metadata.timeCreated === "string") this.createdBy.date = new Date(metadata.timeCreated);
        })
        .catch(() => {})
        .finally(() => {
          callback();
        });
    }
  }

  saveImage(image: Blob, callback: (success: boolean) => void, customMimeType: string | undefined = undefined) {
    const storage = getStorage();
    if (!this.path) return;
    const reference = ref(storage, this.path);

    uploadBytes(reference, image, customMimeType ? { contentType: customMimeType } : undefined)
      .then((snapshot) => {
        getDownloadURL(snapshot.ref)
          .then((downloadURL) => {
            this.fileURL = downloadURL ? downloadURL : undefined;
            this.loaded = true;
            callback(true);
          })
          .catch((error) => {
            console.log(error);
            callback(false);
          });
      })
      .catch((error) => {
        console.log("Error saving file " + error);
        ToastUtilities.presentError(error.code + ": " + error.message);
        callback(false);
      });
  }

  saveAndLoadURL(callback: (success: boolean) => void) {
    const storage = getStorage();
    const saveURL = this.dataURL ?? this.fileURL;
    if (saveURL && this.path) {
      const reference = ref(storage, this.path);

      uploadString(reference, saveURL, "data_url")
        .then(async () => {
          this.loaded = true;
          this.fileURL = await getDownloadURL(reference);
          callback(true);
        })
        .catch((error) => {
          console.log("Error saving file " + error);
          ToastUtilities.presentError(error.code + ": " + error.message);
          callback(false);
        });
    } else {
      callback(false);
    }
  }

  saveDataURL() {
    const storage = getStorage();
    if (!this.dataURL || !this.path) return Promise.reject();

    const reference = ref(storage, this.path);
    return uploadString(reference, this.dataURL, "data_url");
  }

  loadBlob(): Promise<Blob> {
    const storage = getStorage();
    if (!this.path) return Promise.reject();

    const reference = ref(storage, this.path);

    return getDownloadURL(reference).then((url) => {
      return fetch(url).then((response) => {
        return response.blob();
      });
    });
  }

  uploadBlob(blob: File | Blob): Promise<any> {
    const reference = ref(getStorage(), this.path);
    return uploadBytes(reference, blob);
  }

  downloadFile() {
    if (this.fileURL) {
      downloadFirebaseFile(this.fileURL, this.title);
      FirebaseEvents.logEvent("tenant_file_opened", { file_type: this.fileType ?? "" });
    } else {
      ToastUtilities.presentInfo(t("main.loading"));
      this.loadFileURL(() => {
        PopUp.removePopUp();

        if (this.fileURL) {
          downloadFirebaseFile(this.fileURL, this.title);
        }

        FirebaseEvents.logEvent("tenant_file_opened", { file_type: this.fileType ?? "" });
      });
    }
  }

  // Deletes the file from Firebase storage (Deprecated)
  deleteFileFromStorage(callback: (success: boolean) => void) {
    const storage = getStorage();
    if (!this.path) return;
    const reference = ref(storage, this.path);

    deleteObject(reference)
      .then(() => {
        this.deleted = true;
        callback(true);
      })
      .catch((error) => {
        console.log("There was an error deleting the file: " + error.code + ", message: " + error.message + ", details: " + error.details);
        ToastUtilities.presentError(error.code + ": " + error.message);
        callback(false);
      });
  }

  // Returns the firebase save data
  getFirebaseMap(): { [key: string]: any } {
    const returnData: { [key: string]: any } = {
      type: this.fileType,
      name: this.title,
    };
    if (this.path) returnData.path = this.path;
    if (this.size) returnData.size = this.size;
    if (this.externalURL) returnData.externalURL = this.externalURL;
    if (this.createdBy) {
      returnData.createdBy = {};
      if (this.createdBy.user) returnData.createdBy.userID = this.createdBy.user.id;
      if (this.createdBy.date) returnData.createdBy.date = this.createdBy.date;
    }
    return returnData;
  }

  // Checks if fileType is a image type
  isImage(): boolean {
    const imageTypes = ["jpg", "jpeg", "png", "gif", "bmp", "svg"];
    return imageTypes.includes(this.fileType.toLowerCase());
  }

  checkIfFileExists(): Promise<boolean> {
    const storage = getStorage();

    if (!this.path) return Promise.resolve(false);
    const reference = ref(storage, this.path);

    return getDownloadURL(reference)
      .then(() => {
        return true;
      })
      .catch(() => {
        return false;
      });
  }
}

